import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static bodyModalClass = "generic-modal-open"
  static values = {
    closeOnEscape: { type: Boolean, default: true }
  }

  connect() {
    this.previouslyFocusedElement = document.activeElement
    this.inertSiblings = this.collectSiblingElements()
    this.applyInertState()
    this.lockBodyScroll()
    this.boundKeydown = this.keydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
    window.requestAnimationFrame(() => this.focusInitialElement())
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
    this.restoreInertState()
    this.unlockBodyScroll()
    this.restoreFocus()
  }

  close() {
    this.element.remove()
  }

  backdropClose(event) {
    if (event.target === this.element) {
      this.close()
    }
  }

  keydown(event) {
    if (event.key === "Escape") {
      if (this.closeOnEscapeValue) this.close()
      return
    }
    if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  lockBodyScroll() {
    document.body.classList.add(this.constructor.bodyModalClass)
  }

  unlockBodyScroll() {
    document.body.classList.remove(this.constructor.bodyModalClass)
  }

  collectSiblingElements() {
    const siblings = new Set()
    let current = this.element.parentElement

    while (current && current.parentElement) {
      Array.from(current.parentElement.children).forEach((child) => {
        if (child !== current) siblings.add(child)
      })
      if (current === document.body) break
      current = current.parentElement
    }

    return Array.from(siblings)
  }

  applyInertState() {
    this.inertSiblings.forEach((element) => {
      element.dataset.modalPreviouslyAriaHidden = element.getAttribute("aria-hidden") || ""
      element.dataset.modalPreviouslyInert = element.inert ? "true" : "false"
      element.inert = true
      element.setAttribute("aria-hidden", "true")
    })
  }

  restoreInertState() {
    this.inertSiblings.forEach((element) => {
      element.inert = element.dataset.modalPreviouslyInert === "true"
      if (element.dataset.modalPreviouslyAriaHidden) {
        element.setAttribute("aria-hidden", element.dataset.modalPreviouslyAriaHidden)
      } else {
        element.removeAttribute("aria-hidden")
      }
      delete element.dataset.modalPreviouslyAriaHidden
      delete element.dataset.modalPreviouslyInert
    })
  }

  focusableElements() {
    return Array.from(
      this.element.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute("inert"))
  }

  focusInitialElement() {
    const [firstFocusable] = this.focusableElements()
    if (firstFocusable) {
      firstFocusable.focus()
      return
    }

    this.element.setAttribute("tabindex", "-1")
    this.element.focus()
  }

  trapFocus(event) {
    const focusables = this.focusableElements()
    if (focusables.length === 0) {
      event.preventDefault()
      this.element.focus()
      return
    }

    const first = focusables[0]
    const last = focusables[focusables.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  restoreFocus() {
    if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === "function") {
      this.previouslyFocusedElement.focus()
    }
  }
}
